import { FC, ReactNode, RefObject } from 'react';

import { ActionBar, BottomSheet, Button, Drop, HSpacingContainer, NavBar, Text, VSpacing } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

interface ChangeAllDropProps {
    closeDrop: () => void;
    onAfterExit?: () => void;
    onApply: () => void;
    activatorRef: RefObject<HTMLElement>;
    children: ReactNode;
    visible: boolean;
    bottomSheetTitle?: string;
    disabled?: boolean;
    withContentPaddings?: boolean;
}

const TrlKeys = {
    apply: 'MultiCreationAuction.ChangeAllDrop.apply',
    cancel: 'MultiCreationAuction.ChangeAllDrop.cancel',
    currency: 'currency.short',
};

export const ChangeAllDrop: FC<ChangeAllDropProps> = ({
    closeDrop,
    onAfterExit,
    onApply,
    activatorRef,
    children,
    visible,
    bottomSheetTitle,
    disabled = false,
    withContentPaddings = true,
}) => {
    const trls = useTrls();

    const actions = (
        <>
            <Button
                size="medium"
                mode="secondary"
                style="accent"
                onClick={closeDrop}
                stretched
                data-qa="change-all-drop-cancel-button"
            >
                {trls(TrlKeys.cancel)}
            </Button>
            <Button
                size="medium"
                mode="primary"
                style="accent"
                stretched
                onClick={onApply}
                disabled={disabled}
                data-qa="change-all-drop-apply-button"
            >
                {trls(TrlKeys.apply)}
            </Button>
        </>
    );

    return (
        <>
            <Drop
                visible={visible}
                width={375}
                onClose={closeDrop}
                onAfterExit={onAfterExit}
                activatorRef={activatorRef}
                placement="bottom-left"
                footer={<HSpacingContainer default={8}>{actions}</HSpacingContainer>}
                padding={withContentPaddings ? undefined : 0}
                paddingFooter={withContentPaddings ? undefined : 12}
            >
                {children}
            </Drop>
            <BottomSheet
                height="content"
                visible={visible}
                onClose={closeDrop}
                onAfterExit={onAfterExit}
                withContentPaddings={withContentPaddings}
                header={
                    bottomSheetTitle ? (
                        <NavBar showDivider="always">
                            <NavBar.Pane>
                                <NavBar.Actions
                                    left
                                    centered
                                    right={
                                        <CrossOutlinedSize24 padding={8} initialColor="secondary" onClick={closeDrop} />
                                    }
                                >
                                    <NavBar.TitleContainer
                                        title={<Text typography="title-5-semibold">{bottomSheetTitle}</Text>}
                                    />
                                </NavBar.Actions>
                                <VSpacing default={8} />
                            </NavBar.Pane>
                        </NavBar>
                    ) : undefined
                }
                footer={<ActionBar primaryActions={actions} />}
                data-qa="auction-editing-bottom-sheet"
            >
                {children}
                <VSpacing default={8} />
            </BottomSheet>
        </>
    );
};
